import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Send, Loader2, Square, Paperclip, Mic, X, FileText } from 'lucide-react'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { useChat } from '../../contexts/ChatContext'
import { useAudioRecorder } from '../../hooks/useAudioRecorder'
import { useTranscription } from '../../hooks/useTranscription'
import { CommandPicker } from '../commands/CommandPicker'
import { ClinicalCommand } from '../../types/chat'
import { extractCommand, findCommand } from '../../services/commandRegistry'
import { cn } from '../../lib/utils'

interface ChatInputProps {
  patientId?: string
  hidePaperclip?: boolean
}

export const ChatInput: FC<ChatInputProps> = ({ patientId: _patientId, hidePaperclip = false }) => {
  const {
    userInput,
    setUserInput,
    sendMessage,
    stopGeneration,
    isGenerating,
    isCommandPickerOpen,
    setCommandPickerOpen,
    setCommandPrefix,
    context,
    addMessage,
    clearChat,
    files,
    addFile,
    removeFile,
    transcribeAudio
  } = useChat()

  const {
    startRecording,
    stopRecording,
    isRecording,
    audioBlob,
    clearAudioBlob
  } = useAudioRecorder()

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null)

  // Process transcribed audio → append to input, surface failures inline
  useTranscription({
    audioBlob,
    clearAudioBlob,
    transcribeAudio,
    onText: text => setUserInput(prev => (prev ? prev + ' ' : '') + text),
    onError: setTranscriptionError,
  })

  // Clear any prior transcription error, then start a fresh recording
  const handleStartRecording = useCallback(() => {
    setTranscriptionError(null)
    startRecording()
  }, [startRecording])

  useEffect(() => {
    if (userInput.startsWith('/')) {
      setCommandPrefix(userInput.slice(1))
      setCommandPickerOpen(true)
    } else {
      setCommandPickerOpen(false)
      setCommandPrefix('')
    }
  }, [userInput, setCommandPickerOpen, setCommandPrefix])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [userInput])

  const handleCommandSelect = async (command: ClinicalCommand) => {
    setCommandPickerOpen(false)
    setUserInput('')

    if (command.trigger === '/clear') {
      clearChat()
      return
    }

    if (command.trigger === '/help') {
      try {
        const result = await command.execute(context)
        const helpMessage = {
          id: `msg-${Date.now()}`,
          role: 'assistant' as const,
          content: result.content,
          timestamp: new Date().toISOString()
        }
        addMessage(helpMessage)
      } catch (error: any) {
        console.error('Help command failed:', error)
      }
      return
    }

    let messageToSend = ''
    switch (command.trigger) {
      case '/hpi':
        messageToSend = 'Please generate the HPI from the ED notes'
        break
      case '/assessment':
      case '/ap':
        messageToSend = 'Please generate the Assessment and Plan'
        break
      case '/sweep':
      case '/chart':
        messageToSend = 'Please generate a chart sweep from the ED notes'
        break
      case '/ipass':
        messageToSend = 'Please generate an IPASS handoff'
        break
      default:
        messageToSend = command.name
    }

    try {
      await sendMessage(messageToSend)
    } catch (error) {
      console.error('Command execution failed:', error)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await addFile(e.target.files[0], 'paperclip')
      e.target.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const hasUploadingFiles = files.some(f => f.status === 'uploading')
    if ((!userInput.trim() && files.length === 0) || isGenerating || hasUploadingFiles) return

    if (userInput.startsWith('/')) {
      const commandTrigger = extractCommand(userInput)
      if (commandTrigger) {
        const command = findCommand(commandTrigger)
        if (command) {
          await handleCommandSelect(command)
          return
        }
      }
    }

    setUserInput('')

    try {
      await sendMessage(userInput)
    } catch (error) {
      console.error('Failed to send message')
    }
  }

  const canSend = (userInput.trim() || files.length > 0) && !files.some(f => f.status === 'uploading')

  return (
    <div className="w-full flex flex-col gap-2">
      {/* File chips above input */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1">
          {files.map(f => (
            <div
              key={f.id}
              className={cn(
                "inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1.5 rounded-lg",
                "bg-surface-elevated border border-border/50",
                "text-xs text-foreground",
                "hover:border-primary/30 transition-all duration-200"
              )}
            >
              {f.status === 'uploading' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary shrink-0"/>
              ) : (
                <FileText className="h-3.5 w-3.5 text-primary shrink-0"/>
              )}
              <span className="max-w-[150px] truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => removeFile(f.id)}
                className="ml-1 p-1 rounded-md hover:bg-destructive/10 hover:text-destructive transition-colors"
                aria-label={`Remove ${f.name}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="relative flex w-full items-end gap-2">
        <input
          type="file"
          ref={fileInputRef}
          hidden
          onChange={handleFileChange}
          accept=".txt,.md,.xml,.cda,.pdf,.csv,.jpg,.jpeg,.png,text/plain,text/markdown,application/xml,application/pdf,text/csv,image/jpeg,image/png"
        />

        {/* Command Picker */}
        {isCommandPickerOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-2 z-50">
            <CommandPicker onSelect={handleCommandSelect} />
          </div>
        )}

        {/* Single-row input container */}
        <div className={cn(
          "flex-1 flex items-center",
          "bg-surface-elevated border border-border/50 rounded-2xl",
          "focus-within:border-primary/50 focus-within:shadow-glow-sm",
          "transition-all duration-300",
          isRecording && "border-destructive/50 shadow-[0_0_20px_-5px_hsl(var(--destructive)/0.3)]"
        )}>
          {/* Left controls */}
          <div className="flex gap-0.5 p-2">
            {!hidePaperclip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  >
                    <Paperclip className="h-4 w-4"/>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Attach file</p>
                </TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant={isRecording ? "destructive" : "ghost"}
                  onClick={isRecording ? stopRecording : handleStartRecording}
                  aria-label={isRecording ? "Stop recording" : "Voice input"}
                  className={cn(
                    "h-9 w-9 rounded-xl",
                    isRecording
                      ? "animate-pulse"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  )}
                >
                  {isRecording ? (
                    <Loader2 className="h-4 w-4 animate-spin"/>
                  ) : (
                    <Mic className="h-4 w-4"/>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{isRecording ? 'Stop recording' : 'Voice input'}</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value)
              if (transcriptionError) setTranscriptionError(null)
            }}
            placeholder={isRecording ? "Listening..." : "Message Casey..."}
            aria-label="Chat message input"
            disabled={isGenerating}
            className={cn(
              "flex-1 bg-transparent border-none resize-none py-3 px-1",
              "text-sm leading-relaxed placeholder:text-muted-foreground/50",
              "focus:outline-none focus:ring-0",
              "min-h-[48px] max-h-[200px]",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit(e)
              }
            }}
          />

          {/* Send / Stop button */}
          <div className="p-2">
            {isGenerating ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    onClick={stopGeneration}
                    className="h-10 w-10 rounded-xl"
                    aria-label="Stop generation"
                  >
                    <Square className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Stop</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!canSend}
                    className={cn(
                      "h-10 w-10 rounded-xl",
                      "bg-primary text-primary-foreground",
                      "hover:bg-primary/90",
                      "disabled:opacity-30 disabled:cursor-not-allowed",
                      "shadow-glow-sm hover:shadow-glow",
                      "transition-all duration-300"
                    )}
                    aria-label="Send message"
                  >
                    <Send className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">
                    {files.some(f => f.status === 'uploading') ? 'Uploading...' : 'Send (Enter)'}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </form>

      {/* Transcription error */}
      {transcriptionError && (
        <div className="px-3 py-1.5 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg">
          {transcriptionError}
        </div>
      )}
    </div>
  )
}
